import React, { useEffect, useState } from "react";
import { useMutation, useQuery } from "react-query";
import SectionRange from './SectionRange';
import { v4 } from "uuid";
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import { convertTo12Hour } from '../../../../utils/dateTimeFormatter';
import EmptyData from '../../../../components/EmptyData';
import Loader from '../../../../components/loader/Loader';
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import PreAssessment from '../../../../components/PreAssessment/PreAssessment';
import { exams } from "../../../../api/exams";
import { useDispatch } from "react-redux";
import { clearFlaggedQuestion } from "../../../../features/candidate-exam/candidate-exam-slice";
import toast from "react-hot-toast";

function convertDate(isoDate) {
  if (!isoDate) return null;

  const timestamp = Date.parse(isoDate);
  const jsDate = new Date(timestamp);

  const fDate = new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  return fDate.format(jsDate);
}


function capitalizeText(str) {
  if (!str) return null;

  const newText = str.charAt(0).toUpperCase() + str.slice(1);
  return newText;
}

let resultIDS = [
  { id: 1, title: "Questions answered", color: "black" },
  { id: 2, title: "Correct answers", color: "customGreen" },
  { id: 3, title: "Incorrect answers", color: "customOrange" },
  { id: 4, title: "Unanswered questions", color: "slate-400" },
];

export const statusColor = { provisional: "blue-500", fail: "red-500", pass: "customGreen" }

let percentageValues = ["0%", "10", "20", "30", "40", "50", "60", "70", "80", "90", "100%",];

const Result = ({ results, isLoading, index }) => {

  const [value, setValue] = React.useState('one');
  const { t } = useTranslation();
  const url = new URL(window.location.href);
  const user = JSON.parse(sessionStorage.getItem('user'));
  const activeResult = JSON.parse(sessionStorage.getItem('activeResult'));
  //const searchParams = new URLSearchParams(url.search);
  const [searchParams] = useSearchParams();
  const userExamId = searchParams.get('userExamId');
  const ended = searchParams.get('ended');
  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const { isLoading: retakeLoading, mutateAsync } = useMutation(exams.retakeExam)
  const [preAssessmentOpen, setPreAssessmentOpen] = useState(false);
  const [retakeExamData, setRetakeExamData] = useState(null);
  const dispatch = useDispatch()
  const navigate = useNavigate();
  const user_id = JSON.parse(sessionStorage.getItem('user'))?.user?.user_id || sessionStorage.getItem('user_id');

  const retakeExam = async () => {
    if (!parseInt(sessionStorage.getItem('isAllowed'))) {
      return toast.error(t('max-attempts-error'));
    }
    try {
      const response = await mutateAsync({
        userExamId, userId: user_id,
        assessmentId: activeResult?.resultAssessment?.id
      });
      if (response?.data?.number_of_attempts_allowed - response?.data?.number_of_attempts <= 0) {
        return toast.error(t('max-attempts-error'));
      }
      // If allow_proctoring is true, show PreAssessment modal
      if (response?.data?.allow_proctoring) {
        setRetakeExamData(response?.data);
        setPreAssessmentOpen(true);
      } else {
        // proceed directly
        dispatch(clearFlaggedQuestion(null));
        window.sessionStorage.setItem("active-assessment-id", response?.data?.assessment_id);
        window.sessionStorage.setItem("active-assessment-user-id", response?.data?.user_exam_id);
        window.sessionStorage.setItem("active-assessment", JSON.stringify(response?.data));
        window.sessionStorage.setItem("time_left", response?.data?.exam_duration);
        window.sessionStorage.setItem("duration", response?.data?.exam_duration);
        window.sessionStorage.setItem("ql", response?.data?.number_of_questions);
        window.sessionStorage.setItem("starting-index", '0');
        window.sessionStorage.removeItem("unsyncQuestionsParsed");
        const savedAssessmentUserId = localStorage.getItem('lastUserId');
        if (savedAssessmentUserId) {
          if (savedAssessmentUserId !== user_id) {
            localStorage.clear();
          }
        }
        if (response?.data?.number_of_attempts) {
          sessionStorage.setItem('attempt_count', response?.data?.number_of_attempts)
        }
        navigate(`/candidate/assessments/instructions`)
        sessionStorage.setItem('fromRoute', 'result')
      }
    } catch (error) {
      toast.error(error?.response?.data?.message)
    }
  }

  // Handler for PreAssessment completion
  const handlePreAssessmentComplete = () => {
    setPreAssessmentOpen(false);
    if (!retakeExamData) return;
    dispatch(clearFlaggedQuestion(null));
    window.sessionStorage.setItem("active-assessment-id", retakeExamData?.assessment_id);
    window.sessionStorage.setItem("active-assessment-user-id", retakeExamData?.user_exam_id);
    window.sessionStorage.setItem("active-assessment", JSON.stringify(retakeExamData));
    window.sessionStorage.setItem("time_left", retakeExamData?.exam_duration);
    window.sessionStorage.setItem("duration", retakeExamData?.exam_duration);
    window.sessionStorage.setItem("ql", retakeExamData?.number_of_questions);
    window.sessionStorage.setItem("starting-index", '0');
    window.sessionStorage.removeItem("unsyncQuestionsParsed");
    if (retakeExamData?.number_of_attempts) {
      sessionStorage.setItem('attempt_count', retakeExamData?.number_of_attempts)
    }
    navigate(`/candidate/assessments/instructions`)
    sessionStorage.setItem('fromRoute', 'result')
  }


  const [currentResultId, setCurrentResultId] = useState("");
  const attempts = [t('first'), t('second'), t('third'), t('fourth'), t('fifth'), t('sixth'), t('seventh'), t('eighth')];

  const aptitude = results?.groupedResults?.find(
    category => category?.category?.title?.toLowerCase()?.includes('aptitude') || category?.category?.title?.toLowerCase() === 'aptitude'
  )

  const personality = results?.groupedResults?.find(
    category => category?.category?.title?.toLowerCase()?.includes('personality') || category?.category?.title?.toLowerCase() === 'personality'
  )

  const integrity = results?.groupedResults?.find(
    category => category?.category?.title?.toLowerCase()?.includes('integrity') || category?.category?.title?.toLowerCase() === 'integrity'
  )

  useEffect(() => {
    if (!aptitude) setValue('two')
    if (!personality && !aptitude) setValue('three')
  }, [])

  if (isLoading) return <Loader />

  const UserDetails = () => {
    const otherCategory = results?.result?.resultByCategory?.filter((category, index) => (category?.category?.title !== 'Aptitude & Skill' &&
      category?.category?.title !== 'Aptitude'))

    const activeCategoryTitle = results?.result?.resultByCategory[0]?.category?.title;
    const activeCategory = results?.result?.resultByCategory[0];
    // console.log({ otherCategory: results?.result?.resultByCategory[0] });

    return (
      <>
        <div className="my-4 w-full rounded-md border-b pb-3 md:pb-5 border-slate-400">

          <div
            className="mt-3 w-full"
          >
            <div className='bg-white md:flex justify-between flex-wrap gap-3 px-2 py-3 mb-5 font-medium text-xl'>
              Overall Result:
              {parseInt(sessionStorage.getItem('isAllowed')) ?
                <button style={{ backgroundColor: results?.overall_result_status === 'pass' ? '#8EAF55' : '#ef4444' }} className={`text-white  hover:bg-opacity-90 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center`}
                  onClick={retakeExam}
                >
                  Retake Exam
                </button> : null}
            </div>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-3 justify-between">
              <p className="text-baseFont rounded-lg py-8 flex flex-col justify-center gap-3 bg-white shadow-md p-3">
                <span className="font-medium text-center text-lg text-slate-700 pr-3">{t('started-at')}</span>
                <div className="text-center flex justify-center">

                  <span className="">
                    {new Date(results?.result?.started_at).toLocaleString('en-GB', {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                      hour: "numeric",
                      minute: "numeric",
                      second: "numeric",
                      hour12: true,
                    })}
                  </span>
                </div>
              </p>
              <p className="text-baseFont rounded-lg py-8 flex flex-col justify-center gap-3 bg-white shadow-md p-3">
                <span className="font-medium text-center text-lg text-slate-700 pr-3">{t('completed-at')}</span>
                <div className="text-center flex justify-center">
                  {new Date(results?.result?.completed_at).toLocaleString('en-GB', {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                    hour: "numeric",
                    minute: "numeric",
                    second: "numeric",
                    hour12: true,
                  })}
                </div>
              </p>
              <p className="text-baseFont rounded-lg py-8 flex flex-col justify-center gap-3 bg-white shadow-md p-3">
                <span className="font-medium text-center text-lg text-slate-700 pr-3">{t('attempt')}</span>
                <div className="text-center font-medium text-xl flex justify-center">
                  {index + 1}
                </div>
              </p>
              {/* <p className="text-baseFont rounded-lg col-span-3 py-8 flex flex-col justify-center gap-3 bg-white shadow-md p-3">
                <span className="font-medium text-center text-lg text-slate-700 pr-3">
                   The result shown below is for the 50 questions of the Aptitude Section of the Exam. The pass score is 50%
                </span>
                 
              </p> */}

              <p className="text-baseFont rounded-lg py-8 flex flex-col justify-center gap-3 bg-white shadow-md p-3">
                <span className="font-medium text-center text-lg text-slate-700 pr-3">Status</span>
                <div className="text-center flex justify-center">
                  <span
                    className={`text-xl font-medium text-${ended === 'terminated' ? 'red-500' : 'primary' /*(results?.result?.status && statusColor[results?.result?.status])*/}`
                    }
                  >
                    {ended === 'terminated' ? 'Terminated' : 'Completed'} {/*capitalizeText(results?.result?.status)}*/}
                  </span>
                </div>
              </p>
              {activeCategoryTitle === 'Integrity' || activeCategoryTitle?.toLowerCase() === 'personality' ? null :
                <p className="text-baseFont rounded-lg py-8 flex flex-col justify-center gap-3 bg-white shadow-md p-3">
                  <span className="font-medium text-center capitalize text-lg text-slate-700 pr-3">{/*Overall*/}
                    {activeCategoryTitle} Score
                  </span>
                  <div className="text-center flex justify-center">
                    <span
                      className={`text-xl ${activeCategory?.score > 50 ? 'text-primary' : 'text-red-500'
                        }  font-medium `}>
                      {
                        ended === 'terminated' ? 0 :
                          activeCategory?.score
                      }%
                    </span>
                  </div>
                </p>}
              <p className="text-baseFont rounded-lg py-8 flex flex-col justify-center gap-3 bg-white shadow-md p-3">
                <span className="font-medium text-center text-lg text-slate-700 pr-3">
                  {activeCategoryTitle === 'Integrity' ? 'Intergrity Status' :
                    activeCategoryTitle?.toLowerCase() === 'personality' ? 'Personality Status' :
                      `${activeCategoryTitle} Result`}
                </span>
                <div className="text-center w-full flex justify-center">
                  {
                    activeCategoryTitle?.toLowerCase() === 'integrity' || activeCategoryTitle?.toLowerCase() === 'personality' ?
                      <div className="flex items-center justify-center gap-2">
                        <span className="w-10 block h-10 rounded-full" style={{ backgroundColor: activeCategory?.colour === 'amber' ? '#FFBF00' : activeCategory?.colour }}></span>
                        <span className={`text-xl font-medium ${activeCategory?.colour === 'amber' ? 'text-amber-500' : activeCategory?.colour === 'green' ? 'text-primary' : activeCategory?.status?.toLowerCase() === 'pass' ? 'text-primary' : 'text-red-500'}`}>{capitalizeText(activeCategory?.status)}</span>
                      </div>
                      : <span
                        className={`text-xl font-medium ${activeCategory?.status?.toLowerCase() === 'pass' ? 'text-primary' : 'text-red-500' /*(results?.result?.status && statusColor[results?.result?.status])*/}`
                        }
                      >
                        {ended === 'terminated' ? '-' : capitalizeText(
                          activeCategory?.status
                        )}
                      </span>
                  }
                </div>
              </p>
              {activeCategoryTitle === 'Aptitude' || activeCategoryTitle === 'Aptitude & Skill' ? null :
                <p className="text-baseFont rounded-lg py-8 flex flex-col justify-center gap-3 bg-white shadow-md p-3">
                  <span className="font-medium text-center text-lg text-slate-700 pr-3">{'Remark'}</span>
                  <div className="text-center font-medium text-xl flex justify-center">
                    {activeCategory?.remark}
                  </div>
                </p>
              }
            </div>
          </div>

        </div>
        { /*
          otherCategory?.length ?
            <div className="mt-3">
              <h2 className="text-md text-slate-600">Result By Category</h2>
              <section className="flex flex-wrap">
                {
                  otherCategory?.map((category, index) => (
                    <div className="flex items-center gap-3 my-3" key={index}>
                      <span className="text-slate-600">{category?.category?.title}: </span>
                      <div className={`h-3 w-3 rounded-full`} style={{ backgroundColor: category?.color }}></div>
                    </div>
                  ))
                }
              </section>
            </div> : null */}
      </>
    );
  };

  return (
    <>
      <UserDetails />
      {/* PreAssessment Modal for Retake Exam */}
      {retakeExamData?.allow_proctoring && (
        <PreAssessment
          open={preAssessmentOpen}
          onClose={() => setPreAssessmentOpen(false)}
          user_id={sessionStorage.getItem('user_id')}
          user_exam_id={retakeExamData?.user_exam_id}
          assessment_id={retakeExamData?.assessment_id}
          candidate_name={user?.user?.name || user?.name}
          onComplete={handlePreAssessmentComplete}
        />
      )}
      {activeResult?.resultAssessment?.pipeline?.show_section_result ?
        <div className="my-4 w-full rounded-md mt-12">
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center justify-start gap-4">
              {results?.results &&
                results?.results?.map((result) => (
                  <button
                    className={`${result?.id === currentResultId
                      ? "text-slate-700 text-baseFont font-semibold duration-200"
                      : "text-slate-400 text-baseFont"
                      } `}
                    onClick={() => handleResultId(result)}
                  >
                    {result?.category?.title}
                  </button>
                ))}
            </div>
          </div>
          <section className="-mt-12">
            <Box className="p-0 m-0" sx={{
              '& .MuiTabs-flexContainer .Mui-selected': {
                color: '#8EAF55',
                borderBottom: '2px solid #8EAF55',
                outline: 'none',
              },
              '& .MuiTabs-indicator': {
                display: 'none',
                border: 'none'
              },
              '& .MuiSlider-thumb': {
                boxShadow: 'none', // Remove the default box shadow
                border: 'none', // Remove the default border
              },
            }}>
              <Tabs
                value={value}
                onChange={handleChange}
                textColor={undefined}
                indicatorColor={undefined}
                className="text-customGreen"
                aria-label="secondary tabs example"
              >
                {aptitude &&
                  <Tab value="one" className="pb-1 capitalize text-lg font-semibold" label="Aptitude" />
                }
                {personality &&
                  <Tab value="two" className="pb-1 capitalize text-lg font-semibold" label="Personality" />
                }
                {integrity &&
                  <Tab value="three" className="pb-1 capitalize text-lg font-semibold" label="Intergrity" />
                }
              </Tabs>
            </Box>
            <div>
              <div className="p-5 pt-8">
                {
                  aptitude && value === 'one' ?
                    results?.groupedResults?.length ?
                      aptitude.details?.map(section => {
                        return (
                          <div key={v4()} className="md:flex items-center -mt-2">
                            <span className="block md:-mt-8 opacity-80 w-full md:max-w-[180px] text-md whitespace-normal pr-4">
                              {section?.section?.title}
                            </span>
                            {/* <span className={`-mt-8 mr-12 block py-1 w-[78px] text-md ${
                      section?.percentage < 50 ?  ' text-red-500' : 'text-customGreen'
                     } whitespace-nowrap`}>
                        {
                          section?.percentage < 50 ?
                          'failed' : 'passed'
                        }
                     </span>  missin */}
                            <SectionRange
                              value={section?.percentage}
                              colorScheme={section?.percentage < 50 ? "#ef4444" : '#8EAF55'}
                            />
                          </div>
                        )
                      })
                      : <EmptyData text={t('no-result-data')} /> : null
                }
              </div>
              <div className="p-5 pt-0 -mt-8">
                {
                  value === 'two' && personality ?
                    results?.groupedResults?.length ?
                      personality?.details?.map(section => {
                        return (
                          <div key={v4()} className="flex items-center -mt-2">
                            <span className="block -mt-8 opacity-80 w-[200px] text-md whitespace-nowrap">
                              {section?.section?.title}
                            </span>
                            <span className={`-mt-8 mr-12 block py-1 w-[78px] text-md ${section?.status !== 'yes' ? ' text-red-500' : 'text-customGreen'
                              } whitespace-nowrap`}>
                              {
                                section?.status
                              }
                            </span>
                            <SectionRange
                              value={section?.percentage}
                              colorScheme={section?.percentage < 50 ? "#ef4444" : '#8EAF55'}
                            />
                          </div>
                        )
                      })
                      : <EmptyData text='No result data found' /> : null
                }
              </div>
              <div className="p-5 pt-0 -mt-8">
                {
                  value === 'three' && integrity ?
                    results?.groupedResults?.length ?
                      integrity?.details?.map(section => {
                        const failed = Math.ceil((100 / (section?.minimum_score + section?.maximum_score)) * section?.percentage) < 50
                        return (
                          <div key={v4()} className="flex items-center -mt-2">
                            <span className="block -mt-8 opacity-80 w-[160px] text-md whitespace-normal">
                              {section?.section?.title}
                            </span>
                            <span className={`-mt-8 mr-12 block py-1 w-[78px] text-md ${(section?.minimum_score && section?.maximum_score) ? failed
                              ? ' text-red-500' : 'text-customGreen' : section?.percentage < 50 ?
                              ' text-red-500' : 'text-customGreen'
                              } whitespace-nowrap`}>
                              {
                                (section?.minimum_score && section?.maximum_score) ?
                                  failed ? 'failed' : 'passed'
                                  :
                                  section?.percentage < 50 ? 'Failed' : 'Passed'
                              }
                            </span>
                            {
                              (section?.minimum_score && section?.maximum_score) ?
                                <div className="w-[calc(100%-238px)] relative">
                                  <span
                                    style={{
                                      left: `calc(${section?.percentage}%-10px)`
                                    }}
                                    className="absolute bi bi-check text-2xl pb-1 text-white block z-[100] top-[6px]  border border-customGreen rounded-full h-[20px] w-[20px] bg-customGreen" />
                                  <SectionRange
                                    value={
                                      [section?.minimum_score, section?.maximum_score]
                                    }
                                  />
                                </div>
                                :
                                <div className="w-[calc(100%-238px)]">
                                  <SectionRange
                                    value={section?.percentage}
                                    colorScheme={section?.percentage < 50 ? "#ef4444" : '#8EAF55'}
                                  />
                                </div>
                            }
                          </div>
                        )
                      })
                      : <EmptyData text={t('no-result-data')} /> : null
                }
              </div>
            </div>
          </section>
        </div> : null}
    </>
  )
}

export default Result
